/**
 * Guards the Response Status Mapping table against silently rewriting special
 * cases it cannot fully represent.
 *
 * The table's row model is one condition field plus a list of values (or the
 * legacy `second_stp_status` + `response_status` compound). The backend matcher
 * accepts more than that: a list `value` as IN, nested `{operator, clauses}`
 * groups, and any mix of fields. Those shapes are authored directly in
 * Firestore. Without this guard, loading the page and saving it collapses the
 * extra clauses — which widens the rule and stamps wrong codes onto a
 * payer-facing CSV.
 */
import {
  SECOND_STP_STATUS_OPTIONS,
  SftpClause,
  SftpSpecialCase,
} from "../../../../../data-model/nycbsSftpPushConfig";

/** Field name on a clause; `column` is the resubmit-config spelling of `field`. */
export const clauseFieldName = (clause: SftpClause): string =>
  clause.field ?? clause.column ?? "";

/** Second-STP status values stored in `secondStp` by legacy compound special cases. */
export const LEGACY_SECOND_STP_VALUES = new Set(
  SECOND_STP_STATUS_OPTIONS.map((option) => option.value).filter(Boolean),
);

const SECOND_STP_FIELD = "second_stp_status";
const RESPONSE_STATUS_FIELD = "response_status";
/** Ops the backend matcher reads as plain equality — see `EQUALITY_OPS` there. */
const EQUALITY_OPS = ["=", "=="];

/**
 * Returns whether these clauses are the legacy compound shape: exactly one
 * second_stp_status clause holding a known status value, plus response_status
 * clauses.
 *
 * @param clauses - Stored special-case clauses
 * @return True when the compound row shape reproduces these clauses
 */
function isLegacyCompoundShape(clauses: SftpClause[]): boolean {
  const secondStpClauses = clauses.filter(
    (clause) => clauseFieldName(clause) === SECOND_STP_FIELD,
  );
  if (secondStpClauses.length !== 1) return false;
  if (
    !clauses.every((clause) =>
      [SECOND_STP_FIELD, RESPONSE_STATUS_FIELD].includes(
        clauseFieldName(clause),
      ),
    )
  ) {
    return false;
  }
  return LEGACY_SECOND_STP_VALUES.has(
    String(secondStpClauses[0].value ?? "").trim(),
  );
}

/**
 * Returns whether the mapping table's row model holds this special case without
 * loss, so it is safe to render as editable inputs and rebuild on save.
 *
 * @param specialCase - Stored `csv.response_status_mapping.special_cases` entry
 * @return True when a round trip through the row model is lossless
 */
export function isEditableSpecialCase(specialCase: SftpSpecialCase): boolean {
  const clauses = specialCase.clauses ?? [];
  if (clauses.length === 0) return false;
  // A nested group has no row representation — the row keeps only its siblings.
  // `clauses: null` is a leaf, matching the backend's null-vs-missing rule.
  if (clauses.some((clause) => clause.clauses != null)) return false;
  // A row holds one value per tag, so a multi-entry IN list loses all but the first.
  if (
    clauses.some(
      (clause) => Array.isArray(clause.value) && clause.value.length > 1,
    )
  ) {
    return false;
  }
  // The row rebuilds bare equality, which the backend reads as `=`. Only a
  // non-equality `op` is genuinely lost.
  if (
    clauses.some(
      (clause) => clause.op != null && !EQUALITY_OPS.includes(clause.op.trim()),
    )
  ) {
    return false;
  }
  // A blank value is dropped on rebuild, turning a never-matching AND into a match.
  if (
    clauses.some(
      (clause) =>
        String(
          Array.isArray(clause.value)
            ? (clause.value[0] ?? "")
            : (clause.value ?? ""),
        ).trim() === "",
    )
  ) {
    return false;
  }

  const fields = [...new Set(clauses.map(clauseFieldName))];
  if (fields.some((field) => field === "")) return false;

  if (fields.length === 1) {
    // Multi-value single-field rows always rebuild as OR; an AND would widen.
    return clauses.length === 1 || specialCase.operator === "OR";
  }
  // Compound rows always rebuild as AND; an OR would narrow.
  return specialCase.operator !== "OR" && isLegacyCompoundShape(clauses);
}

/**
 * Renders one clause as a compact condition string.
 *
 * @param clause - Leaf clause or nested group
 * @return Human-readable condition text
 */
function describeClause(clause: SftpClause): string {
  if (clause.clauses) {
    return `(${describeClauses(clause.clauses, clause.operator ?? "AND")})`;
  }
  const field = clauseFieldName(clause) || "?";
  if (Array.isArray(clause.value)) {
    return `${field} IN (${clause.value.join(", ")})`;
  }
  return `${field} ${clause.op ?? "="} ${clause.value ?? ""}`;
}

/**
 * Joins clauses with their conjunction.
 *
 * @param clauses - Clauses in the group
 * @param operator - Conjunction joining them
 * @return Human-readable condition text
 */
function describeClauses(clauses: SftpClause[], operator: string): string {
  if (clauses.length === 0) return "(empty)";
  return clauses.map(describeClause).join(` ${operator} `);
}

/**
 * Renders a special case the table cannot edit as read-only condition text.
 *
 * @param specialCase - Stored special-case entry
 * @return Human-readable condition text, e.g. `route_via IN (A, B) AND x = y`
 */
export function describeSpecialCase(specialCase: SftpSpecialCase): string {
  return describeClauses(
    specialCase.clauses ?? [],
    specialCase.operator ?? "AND",
  );
}
